import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../services/api';

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const moneyFmt = new Intl.NumberFormat('es-EC', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const fmt = (n) => moneyFmt.format(Number(n || 0));
const safe = (v) => (v == null || v === '' ? '-' : String(v));
const text = (v) => (v == null || v === '' ? '' : String(v));
const number = (v) => Number(v || 0);

const nombreArchivoSeguro = (v) =>
  String(v || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const normalizarBusqueda = (v) =>
  String(v || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const tipoPago = (r) => (r.tipoDocumento === 'PP' ? 'Prepago' : 'Pago');
const numeroNotaCredito = (r) => `#${safe(r.numero_nc || r.idpago)}`;
const origenNotaCredito = (r) =>
  r.idpago_origen
    ? `#${safe(r.numero_origen || r.idpago_origen)}${
        r.relacionado_nombre ? ` - ${r.relacionado_nombre}` : ''
      }`
    : '-';

const columnasPorReporte = (tipoReporte) =>
  tipoReporte === 'notas_credito'
    ? [
        { label: 'Fecha', value: (r) => safe(r.fecha), width: 12, pdfWidth: 46 },
        { label: 'Lado', value: (r) => safe(r.lado), width: 13, pdfWidth: 50 },
        { label: 'Tercero', value: (r) => safe(r.tercero_nombre), width: 28, pdfWidth: 110 },
        { label: 'NC', value: numeroNotaCredito, width: 12, pdfWidth: 42 },
        { label: 'Origen', value: origenNotaCredito, width: 32, pdfWidth: 130 },
        {
          label: 'Items',
          value: (r) => number(r.nc_credito_items),
          money: true,
          width: 12,
          pdfWidth: 43
        },
        { label: 'Flete', value: (r) => number(r.nc_flete), money: true, width: 12, pdfWidth: 43 },
        { label: 'Otros', value: (r) => number(r.nc_otros), money: true, width: 12, pdfWidth: 43 },
        { label: 'Total', value: (r) => number(r.valor), money: true, width: 12, pdfWidth: 46 },
        { label: 'Facturas', value: (r) => safe(r.facturas), width: 18, pdfWidth: 65 },
        { label: 'Observaciones', value: (r) => safe(r.observaciones), width: 45, pdfWidth: 175 }
      ]
    : [
        { label: 'Fecha', value: (r) => safe(r.fecha), width: 12, pdfWidth: 50 },
        { label: 'Lado', value: (r) => safe(r.lado), width: 13, pdfWidth: 50 },
        { label: 'Tercero', value: (r) => safe(r.tercero_nombre), width: 30, pdfWidth: 105 },
        { label: 'Tipo', value: tipoPago, width: 12, pdfWidth: 42 },
        { label: 'Comprobante', value: (r) => safe(r.numero_comprobante), width: 18, pdfWidth: 65 },
        { label: 'Banco', value: (r) => safe(r.banco_nombre), width: 22, pdfWidth: 75 },
        { label: 'Valor', value: (r) => number(r.valor), money: true, width: 12, pdfWidth: 55 },
        {
          label: 'Costo banco',
          value: (r) => number(r.costo_bancario),
          money: true,
          width: 14,
          pdfWidth: 55
        },
        { label: 'Facturas', value: (r) => safe(r.facturas), width: 22, pdfWidth: 80 },
        { label: 'Observaciones', value: (r) => safe(r.observaciones), width: 45, pdfWidth: 190 }
      ];

const th = {
  padding: '9px 10px',
  borderBottom: '1px solid #d8dde6',
  background: '#f4f6f8',
  fontWeight: 700,
  textAlign: 'left',
  whiteSpace: 'nowrap'
};

const td = {
  padding: '8px 10px',
  borderBottom: '1px solid #edf0f4',
  verticalAlign: 'top'
};

const tdMoney = {
  ...td,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums'
};

export default function ModalReportesMasterCartera({ show, onClose }) {
  const [tipoReporte, setTipoReporte] = useState('pagos');
  const [ladoFiltro, setLadoFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [desde, setDesde] = useState(inicioMesISO());
  const [hasta, setHasta] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [accionPdfId, setAccionPdfId] = useState(null);
  const [seleccionados, setSeleccionados] = useState([]);
  const [registroVista, setRegistroVista] = useState(null);
  const [pdfVista, setPdfVista] = useState(null);
  const [cargandoVista, setCargandoVista] = useState(false);
  const [accionVista, setAccionVista] = useState(null);
  const pdfVistaUrlRef = useRef(null);
  const vistaRequestIdRef = useRef(0);

  const esNotasCredito = tipoReporte === 'notas_credito';

  const titulo = esNotasCredito ? 'Reporte master de notas de credito' : 'Reporte master de cartera';

  const endpoint =
    esNotasCredito
      ? '/api/cartera/reportes-master/notas-credito'
      : '/api/cartera/reportes-master/pagos';

  const rowsFiltradas = useMemo(() => {
    let base =
      ladoFiltro === 'todos'
        ? rows
        : rows.filter((r) => String(r.lado || '').toLowerCase() === ladoFiltro);

    const q = normalizarBusqueda(busqueda).trim();
    if (!q) return base;

    return base.filter((r) => {
      const valores = columnasPorReporte(tipoReporte).map((c) => c.value(r));
      valores.push(r.idpago, r.tipoMovimiento, r.lado, r.correo, r.numero_nc, r.numero_origen);
      return normalizarBusqueda(valores.join(' ')).includes(q);
    });
  }, [busqueda, ladoFiltro, rows, tipoReporte]);

  const keyRegistro = useCallback(
    (r) => `${tipoReporte}-${String(r?.tipoMovimiento || '').toUpperCase()}-${r?.idpago}`,
    [tipoReporte]
  );

  const seleccionadosSet = useMemo(() => new Set(seleccionados), [seleccionados]);

  const rowsSeleccionadas = useMemo(
    () => rowsFiltradas.filter((r) => seleccionadosSet.has(keyRegistro(r))),
    [keyRegistro, rowsFiltradas, seleccionadosSet]
  );

  const todosVisiblesSeleccionados = useMemo(
    () =>
      rowsFiltradas.length > 0 &&
      rowsFiltradas.every((r) => seleccionadosSet.has(keyRegistro(r))),
    [keyRegistro, rowsFiltradas, seleccionadosSet]
  );

  const algunosVisiblesSeleccionados = useMemo(
    () =>
      rowsFiltradas.some((r) => seleccionadosSet.has(keyRegistro(r))) &&
      !todosVisiblesSeleccionados,
    [keyRegistro, rowsFiltradas, seleccionadosSet, todosVisiblesSeleccionados]
  );

  const totalValor = useMemo(
    () => rowsFiltradas.reduce((s, r) => s + Number(r.valor || 0), 0),
    [rowsFiltradas]
  );

  const totalCosto = useMemo(
    () => rowsFiltradas.reduce((s, r) => s + Number(r.costo_bancario || 0), 0),
    [rowsFiltradas]
  );

  const cargar = useCallback(async () => {
    if (!desde || !hasta) {
      alert('Selecciona desde y hasta.');
      return;
    }
    if (desde > hasta) {
      alert('La fecha desde no puede ser mayor que hasta.');
      return;
    }

    setCargando(true);
    setRows([]);
    setSeleccionados([]);
    try {
      const { data } = await api.get(endpoint, { params: { desde, hasta } });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error cargando reporte master:', err);
      alert(err?.response?.data?.error || 'No se pudo cargar el reporte.');
    } finally {
      setCargando(false);
    }
  }, [desde, endpoint, hasta]);

  useEffect(() => {
    if (show) cargar();
  }, [show, tipoReporte, cargar]);

  useEffect(() => {
    if (!show) {
      setRows([]);
      setSeleccionados([]);
    }
  }, [show]);

  useEffect(() => {
    setSeleccionados([]);
  }, [tipoReporte]);

  const columnas = useMemo(() => columnasPorReporte(tipoReporte), [tipoReporte]);

  const nombreBaseArchivo = useMemo(
    () =>
      `${nombreArchivoSeguro(titulo)}${ladoFiltro !== 'todos' ? `_${ladoFiltro}` : ''}_${desde}_${hasta}`,
    [desde, hasta, ladoFiltro, titulo]
  );

  const footerExcel = useCallback(() => {
    const footer = Array(columnas.length).fill('');

    if (tipoReporte === 'pagos') {
      footer[5] = 'Totales';
      footer[6] = totalValor;
      footer[7] = totalCosto;
    } else {
      footer[7] = 'Total';
      footer[8] = totalValor;
    }

    return footer;
  }, [columnas.length, tipoReporte, totalCosto, totalValor]);

  const validarExportacion = () => {
    if (cargando) return false;
    if (!rowsFiltradas.length) {
      alert('No hay registros para descargar.');
      return false;
    }
    return true;
  };

  const exportarExcel = () => {
    if (!validarExportacion()) return;

    const headers = columnas.map((c) => c.label);
    const body = rowsFiltradas.map((r) =>
      columnas.map((c) => {
        const value = c.value(r);
        return c.money ? number(value) : safe(value);
      })
    );

    const resumen = [`Registros: ${rowsFiltradas.length}`, `Total: ${fmt(totalValor)}`];
    if (ladoFiltro !== 'todos') resumen.push(`Lado: ${safe(ladoFiltro)}`);
    if (busqueda.trim()) resumen.push(`Busqueda: ${busqueda.trim()}`);
    if (tipoReporte === 'pagos') resumen.push(`Costo banco: ${fmt(totalCosto)}`);

    const tableStartRow = 4;
    const aoa = [[titulo], [`Rango: ${desde} a ${hasta}`], resumen, [], headers, ...body, footerExcel()];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = columnas.map((c) => ({ wch: c.width }));
    ws['!merges'] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: Math.max(columnas.length - 1, 0) }
      }
    ];

    const footerRowIndex = tableStartRow + 1 + body.length;
    for (let rowIndex = tableStartRow + 1; rowIndex <= footerRowIndex; rowIndex += 1) {
      columnas.forEach((c, colIndex) => {
        if (!c.money) return;
        const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        if (ws[address]) ws[address].z = '"$"#,##0.00';
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    XLSX.writeFile(wb, `${nombreBaseArchivo}.xlsx`);
  };

  const exportarPDF = () => {
    if (!validarExportacion()) return;

    const doc = new jsPDF('landscape', 'pt', 'a4');
    const marginX = 24;
    let y = 34;

    doc.setFontSize(14);
    doc.text(titulo, marginX, y);
    y += 16;

    const resumen = [
      `Rango: ${desde} a ${hasta}`,
      `Registros: ${rowsFiltradas.length}`,
      `Total: ${fmt(totalValor)}`
    ];
    if (ladoFiltro !== 'todos') resumen.push(`Lado: ${safe(ladoFiltro)}`);
    if (busqueda.trim()) resumen.push(`Busqueda: ${busqueda.trim()}`);
    if (tipoReporte === 'pagos') resumen.push(`Costo banco: ${fmt(totalCosto)}`);

    doc.setFontSize(9);
    const metaLines = doc.splitTextToSize(
      resumen.join('   |   '),
      doc.internal.pageSize.getWidth() - marginX * 2
    );
    doc.text(metaLines, marginX, y);
    y += metaLines.length * 11 + 8;

    const footer = footerExcel().map((value, index) => {
      if (value === '') return '';
      return columnas[index]?.money ? fmt(value) : text(value);
    });

    autoTable(doc, {
      head: [columnas.map((c) => c.label)],
      body: rowsFiltradas.map((r) =>
        columnas.map((c) => {
          const value = c.value(r);
          return c.money ? fmt(value) : safe(value);
        })
      ),
      foot: [footer],
      showFoot: 'lastPage',
      startY: y,
      margin: { left: marginX, right: marginX, bottom: 24 },
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak', valign: 'top' },
      headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [244, 246, 248], textColor: 20, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 251, 253] },
      columnStyles: columnas.reduce((acc, c, index) => {
        acc[index] = {
          cellWidth: c.pdfWidth,
          halign: c.money ? 'right' : 'left'
        };
        return acc;
      }, {}),
      didDrawPage: () => {
        const page = `Pagina ${doc.internal.getNumberOfPages()}`;
        doc.setFontSize(8);
        doc.text(page, doc.internal.pageSize.getWidth() - marginX, doc.internal.pageSize.getHeight() - 10, {
          align: 'right'
        });
      }
    });

    doc.save(`${nombreBaseArchivo}.pdf`);
  };

  const toggleSeleccion = (r) => {
    const key = keyRegistro(r);
    setSeleccionados((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const toggleTodosVisibles = () => {
    const visibles = rowsFiltradas.map((r) => keyRegistro(r));
    setSeleccionados((prev) => {
      if (todosVisiblesSeleccionados) {
        const visiblesSet = new Set(visibles);
        return prev.filter((key) => !visiblesSet.has(key));
      }

      const next = new Set(prev);
      visibles.forEach((key) => next.add(key));
      return Array.from(next);
    });
  };

  const validarSeleccionPdf = () => {
    if (!rowsSeleccionadas.length) {
      alert('Selecciona al menos un registro.');
      return false;
    }
    return true;
  };

  const urlPdfRegistro = (r) => {
    const lado = String(r.tipoMovimiento || '').toUpperCase() === 'P' ? 'P' : 'C';
    const params = { lado };

    if (tipoReporte === 'notas_credito') {
      if (lado === 'P') params.proveedor_id = r.idtercero;
      return {
        url: `/api/cartera/reporte-nc/${r.idpago}`,
        params,
        filename: `NC_${nombreArchivoSeguro(r.numero_nc || r.idpago)}_${lado}.pdf`
      };
    }

    return {
      url: `/api/cartera/reporte-comprobante/${r.idpago}`,
      params,
      filename: `Comprobante_${lado}_${nombreArchivoSeguro(r.numero_comprobante || r.idpago)}.pdf`
    };
  };

  const obtenerBlobPdfRegistro = async (r) => {
    const cfg = urlPdfRegistro(r);
    const { data } = await api.get(cfg.url, {
      params: cfg.params,
      responseType: 'blob'
    });
    return { blob: new Blob([data], { type: 'application/pdf' }), filename: cfg.filename };
  };

  const limpiarPdfVista = useCallback(() => {
    if (pdfVistaUrlRef.current) {
      URL.revokeObjectURL(pdfVistaUrlRef.current);
      pdfVistaUrlRef.current = null;
    }
    setPdfVista(null);
  }, []);

  const cerrarVistaPrevia = useCallback(() => {
    vistaRequestIdRef.current += 1;
    limpiarPdfVista();
    setRegistroVista(null);
    setCargandoVista(false);
    setAccionVista(null);
  }, [limpiarPdfVista]);

  useEffect(() => {
    if (!show) cerrarVistaPrevia();
  }, [show, cerrarVistaPrevia]);

  useEffect(
    () => () => {
      vistaRequestIdRef.current += 1;
      if (pdfVistaUrlRef.current) URL.revokeObjectURL(pdfVistaUrlRef.current);
    },
    []
  );

  const abrirVistaPrevia = async (r) => {
    if (!r?.idpago) return;

    const requestId = vistaRequestIdRef.current + 1;
    vistaRequestIdRef.current = requestId;
    limpiarPdfVista();
    setRegistroVista(r);
    setCargandoVista(true);
    setAccionVista(null);

    try {
      const archivo = await obtenerBlobPdfRegistro(r);
      if (requestId !== vistaRequestIdRef.current) return;

      const url = URL.createObjectURL(archivo.blob);
      pdfVistaUrlRef.current = url;
      setPdfVista({ ...archivo, url });
    } catch (err) {
      if (requestId !== vistaRequestIdRef.current) return;
      console.error('Error cargando vista previa del PDF:', err);
      alert(err?.response?.data?.error || 'No se pudo abrir la vista previa del archivo.');
    } finally {
      if (requestId === vistaRequestIdRef.current) setCargandoVista(false);
    }
  };

  const descargarPdfSeleccionados = async () => {
    if (!validarSeleccionPdf()) return;

    try {
      setAccionPdfId('descargar');
      for (const r of rowsSeleccionadas) {
        const { blob, filename } = await obtenerBlobPdfRegistro(r);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
    } catch (err) {
      console.error('Error descargando PDF individual:', err);
      alert(err?.response?.data?.error || 'No se pudieron descargar los PDF.');
    } finally {
      setAccionPdfId(null);
    }
  };

  const imprimirBlob = (blob) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } finally {
          setTimeout(() => {
            iframe.remove();
            URL.revokeObjectURL(url);
            resolve();
          }, 1200);
        }
      };
    });

  const imprimirPdfSeleccionados = async () => {
    if (!validarSeleccionPdf()) return;

    try {
      setAccionPdfId('imprimir');
      for (const r of rowsSeleccionadas) {
        const { blob } = await obtenerBlobPdfRegistro(r);
        await imprimirBlob(blob);
      }
    } catch (err) {
      console.error('Error imprimiendo PDF individual:', err);
      alert(err?.response?.data?.error || 'No se pudieron imprimir los PDF.');
    } finally {
      setAccionPdfId(null);
    }
  };

  const guardarPdfVista = () => {
    if (!pdfVista?.url) return;
    const a = document.createElement('a');
    a.href = pdfVista.url;
    a.download = pdfVista.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const imprimirPdfVista = async () => {
    if (!pdfVista?.blob) return;
    try {
      setAccionVista('imprimir');
      await imprimirBlob(pdfVista.blob);
    } catch (err) {
      console.error('Error imprimiendo la vista previa:', err);
      alert('No se pudo imprimir el archivo.');
    } finally {
      setAccionVista(null);
    }
  };

  const enviarNotaCreditoVista = async () => {
    if (!registroVista || tipoReporte !== 'notas_credito') return;

    const confirmar = window.confirm(
      `¿Enviar ${numeroNotaCredito(registroVista)} por correo a ${registroVista.correo}?`
    );
    if (!confirmar) return;

    const lado = String(registroVista.tipoMovimiento || '').toUpperCase() === 'P' ? 'P' : 'C';
    const idpagoCliente =
      lado === 'P'
        ? Number(registroVista.idpago_origen || registroVista.idpago)
        : Number(registroVista.idpago);
    const params = {
      target: lado === 'P' ? 'proveedores' : 'cliente'
    };
    if (lado === 'P') params.proveedor_id = registroVista.idtercero;

    try {
      setAccionVista('correo');
      const { data } = await api.post(
        `/api/cartera/reporte-nc/${idpagoCliente}/enviar`,
        null,
        { params }
      );

      if (Number(data?.enviados || 0) < 1) {
        throw new Error('No se encontró un correo válido para el destinatario.');
      }
      alert(`Nota de crédito enviada correctamente a ${registroVista.correo}.`);
    } catch (err) {
      console.error('Error enviando nota de crédito:', err);
      alert(err?.response?.data?.error || err?.message || 'No se pudo enviar la nota de crédito.');
    } finally {
      setAccionVista(null);
    }
  };

  const esNotaCreditoVista = tipoReporte === 'notas_credito';
  const ladoVista =
    String(registroVista?.tipoMovimiento || '').toUpperCase() === 'P' ? 'Proveedor' : 'Cliente';
  const correoVista = text(registroVista?.correo).trim();
  const tieneCorreoVista = Boolean(correoVista && correoVista !== '-');

  const tituloVista = esNotaCreditoVista
    ? `Nota de crédito ${registroVista ? numeroNotaCredito(registroVista) : ''}`
    : `${ladoVista === 'Cliente' ? 'Comprobante de cobro' : 'Comprobante de pago'} ${
        registroVista?.numero_comprobante && registroVista.numero_comprobante !== '-'
          ? registroVista.numero_comprobante
          : `#${registroVista?.idpago || ''}`
      }`;

  const renderPagos = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          <th style={{ ...th, width: 46, textAlign: 'center' }}>
            <Checkbox
              size="small"
              checked={todosVisiblesSeleccionados}
              indeterminate={algunosVisiblesSeleccionados}
              onChange={toggleTodosVisibles}
              disabled={!rowsFiltradas.length}
            />
          </th>
          <th style={{ ...th, width: 92, textAlign: 'center' }}>Archivo</th>
          <th style={th}>Fecha</th>
          <th style={th}>Lado</th>
          <th style={th}>Tercero</th>
          <th style={th}>Tipo</th>
          <th style={th}>Comprobante</th>
          <th style={th}>Banco</th>
          <th style={{ ...th, textAlign: 'right' }}>Valor</th>
          <th style={{ ...th, textAlign: 'right' }}>Costo banco</th>
          <th style={th}>Facturas</th>
          <th style={th}>Observaciones</th>
        </tr>
      </thead>
      <tbody>
        {rowsFiltradas.map((r) => (
          <tr key={r.idpago}>
            <td style={{ ...td, textAlign: 'center' }}>
              <Checkbox
                size="small"
                checked={seleccionadosSet.has(keyRegistro(r))}
                onChange={() => toggleSeleccion(r)}
              />
            </td>
            <td style={{ ...td, textAlign: 'center' }}>
              <Button size="small" variant="outlined" onClick={() => abrirVistaPrevia(r)}>
                Ver
              </Button>
            </td>
            <td style={td}>{safe(r.fecha)}</td>
            <td style={td}>{safe(r.lado)}</td>
            <td style={td}>{safe(r.tercero_nombre)}</td>
            <td style={td}>{r.tipoDocumento === 'PP' ? 'Prepago' : 'Pago'}</td>
            <td style={td}>{safe(r.numero_comprobante)}</td>
            <td style={td}>{safe(r.banco_nombre)}</td>
            <td style={tdMoney}>{fmt(r.valor)}</td>
            <td style={tdMoney}>{fmt(r.costo_bancario)}</td>
            <td style={td}>{safe(r.facturas)}</td>
            <td style={td}>{safe(r.observaciones)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderNotasCredito = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          <th style={{ ...th, width: 46, textAlign: 'center' }}>
            <Checkbox
              size="small"
              checked={todosVisiblesSeleccionados}
              indeterminate={algunosVisiblesSeleccionados}
              onChange={toggleTodosVisibles}
              disabled={!rowsFiltradas.length}
            />
          </th>
          <th style={{ ...th, width: 92, textAlign: 'center' }}>Archivo</th>
          <th style={th}>Fecha</th>
          <th style={th}>Lado</th>
          <th style={th}>Tercero</th>
          <th style={th}>NC</th>
          <th style={th}>Origen</th>
          <th style={{ ...th, textAlign: 'right' }}>Items</th>
          <th style={{ ...th, textAlign: 'right' }}>Flete</th>
          <th style={{ ...th, textAlign: 'right' }}>Otros</th>
          <th style={{ ...th, textAlign: 'right' }}>Total</th>
          <th style={th}>Facturas</th>
          <th style={th}>Observaciones</th>
        </tr>
      </thead>
      <tbody>
        {rowsFiltradas.map((r) => (
          <tr key={`${r.tipoMovimiento}-${r.idpago}`}>
            <td style={{ ...td, textAlign: 'center' }}>
              <Checkbox
                size="small"
                checked={seleccionadosSet.has(keyRegistro(r))}
                onChange={() => toggleSeleccion(r)}
              />
            </td>
            <td style={{ ...td, textAlign: 'center' }}>
              <Button size="small" variant="outlined" onClick={() => abrirVistaPrevia(r)}>
                Ver
              </Button>
            </td>
            <td style={td}>{safe(r.fecha)}</td>
            <td style={td}>{safe(r.lado)}</td>
            <td style={td}>{safe(r.tercero_nombre)}</td>
            <td style={td}>{numeroNotaCredito(r)}</td>
            <td style={td}>
              {origenNotaCredito(r)}
            </td>
            <td style={tdMoney}>{fmt(r.nc_credito_items)}</td>
            <td style={tdMoney}>{fmt(r.nc_flete)}</td>
            <td style={tdMoney}>{fmt(r.nc_otros)}</td>
            <td style={tdMoney}>{fmt(r.valor)}</td>
            <td style={td}>{safe(r.facturas)}</td>
            <td style={td}>{safe(r.observaciones)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
    <Dialog open={show} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent dividers>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'end' }}
          sx={{ mb: 1.25 }}
        >
          <Box sx={{ minWidth: 260, flexGrow: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Tipo de reporte
            </Typography>
            <select
              value={tipoReporte}
              onChange={(e) => setTipoReporte(e.target.value)}
              style={{ width: '100%', height: 40, padding: '0 10px' }}
            >
              <option value="pagos">Reporte master de cartera</option>
              <option value="notas_credito">Reporte master de notas de credito</option>
            </select>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Desde
            </Typography>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              style={{ height: 40, padding: '0 10px' }}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Hasta
            </Typography>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              style={{ height: 40, padding: '0 10px' }}
            />
          </Box>

          <Box sx={{ minWidth: 170 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Lado
            </Typography>
            <select
              value={ladoFiltro}
              onChange={(e) => setLadoFiltro(e.target.value)}
              style={{ width: '100%', height: 40, padding: '0 10px' }}
            >
              <option value="todos">Todos</option>
              <option value="cliente">Clientes</option>
              <option value="proveedor">Proveedores</option>
            </select>
          </Box>

          <Box sx={{ minWidth: 260 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Buscar
            </Typography>
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre, factura, comprobante, observacion"
              style={{ width: '100%', height: 40, padding: '0 10px' }}
            />
          </Box>

          <Button
            variant="contained"
            onClick={cargar}
            disabled={cargando}
            sx={{ minWidth: 110, height: 40, textTransform: 'none', fontWeight: 600 }}
          >
            Actualizar
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5,
            p: 1,
            border: '1px solid #e1e6ed',
            borderRadius: 1.5,
            bgcolor: '#f8fafc',
            '& .MuiButton-root': {
              minWidth: { xs: 'calc(50% - 4px)', sm: 112 },
              minHeight: 40,
              m: 0,
              textTransform: 'none',
              fontWeight: 600
            }
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => abrirVistaPrevia(rowsSeleccionadas[0])}
            disabled={cargando || !!accionPdfId || rowsSeleccionadas.length !== 1}
          >
            Vista previa
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={imprimirPdfSeleccionados}
            disabled={cargando || !!accionPdfId || rowsSeleccionadas.length === 0}
          >
            {accionPdfId === 'imprimir' ? 'Imprimiendo...' : 'Imprimir'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={descargarPdfSeleccionados}
            disabled={cargando || !!accionPdfId || rowsSeleccionadas.length === 0}
          >
            {accionPdfId === 'descargar' ? 'Descargando...' : 'Descargar'}
          </Button>

          <Button
            variant="outlined"
            onClick={exportarExcel}
            disabled={cargando || rowsFiltradas.length === 0}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            onClick={exportarPDF}
            disabled={cargando || rowsFiltradas.length === 0}
          >
            PDF
          </Button>
        </Box>

        <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
          <Typography variant="body2">
            Registros: <b>{rowsFiltradas.length}</b>
          </Typography>
          <Typography variant="body2">
            Total: <b>{fmt(totalValor)}</b>
          </Typography>
          {tipoReporte === 'pagos' && (
            <Typography variant="body2">
              Costo banco: <b>{fmt(totalCosto)}</b>
            </Typography>
          )}
          <Typography variant="body2">
            Seleccionados: <b>{rowsSeleccionadas.length}</b>
          </Typography>
          {busqueda.trim() && (
            <Typography variant="body2">
              Busqueda: <b>{busqueda.trim()}</b>
            </Typography>
          )}
        </Stack>

        {cargando && <LinearProgress sx={{ mb: 1 }} />}

        <Box sx={{ border: '1px solid #dfe3ea', borderRadius: 1, overflow: 'auto', maxHeight: 560 }}>
          {!cargando && rowsFiltradas.length === 0 ? (
            <Box sx={{ p: 2, color: '#667085' }}>No hay registros en el rango seleccionado.</Box>
          ) : tipoReporte === 'notas_credito' ? (
            renderNotasCredito()
          ) : (
            renderPagos()
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={Boolean(registroVista)}
      onClose={cerrarVistaPrevia}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: '95vw',
          height: '90vh',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
        >
          <Box>
            <Typography variant="h6">Vista previa - {tituloVista}</Typography>
            <Typography variant="body2" color="text.secondary">
              {registroVista?.tercero_nombre || 'Sin tercero'} · {ladoVista} ·{' '}
              {registroVista?.fecha || 'Sin fecha'}
            </Typography>
          </Box>
          {esNotaCreditoVista && (
            <Typography variant="body2" color={tieneCorreoVista ? 'text.secondary' : 'error.main'}>
              {tieneCorreoVista ? `Correo: ${correoVista}` : 'El tercero no tiene correo registrado'}
            </Typography>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {cargandoVista && <LinearProgress sx={{ mb: 1 }} />}
        {cargandoVista ? (
          <Box sx={{ p: 2, color: 'text.secondary' }}>Generando vista previa...</Box>
        ) : pdfVista?.url ? (
          <Box
            component="iframe"
            src={pdfVista.url}
            title={tituloVista}
            sx={{ flex: 1, width: '100%', minHeight: 0, border: 0, borderRadius: 1 }}
          />
        ) : (
          <Box sx={{ p: 2, color: 'text.secondary' }}>No hay una vista previa disponible.</Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        {esNotaCreditoVista && (
          <Button
            variant="contained"
            onClick={enviarNotaCreditoVista}
            disabled={!pdfVista || !tieneCorreoVista || !!accionVista}
          >
            {accionVista === 'correo' ? 'Enviando...' : 'Enviar por correo'}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={imprimirPdfVista}
          disabled={!pdfVista || !!accionVista}
        >
          {accionVista === 'imprimir' ? 'Imprimiendo...' : 'Imprimir'}
        </Button>
        <Button variant="outlined" onClick={guardarPdfVista} disabled={!pdfVista || !!accionVista}>
          Guardar PDF
        </Button>
        <Button onClick={cerrarVistaPrevia} disabled={!!accionVista}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
